import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { IconChevronCircle } from '../HeaderIcons';
import PromoModalRouter from './PromoModalRouter';
import PromoCard, { PromoCardPlaceholder } from '../promos/PromoCard';
import { filterPromos, resolvePromoClick, setPromoUrl } from '../promos/promoUtils';
import { lockScroll, unlockScroll } from '../../scrollLock';
import { useLocale } from '../../context';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSiteContent } from '../../context/SiteContentContext';
import {
  getPromoCategories,
  getPromoFilters,
  isTournamentCategory,
} from '../../i18n/homeContent';

export default function PromotionsBlock({ onNavigate, onRegister }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const isMobile = useIsMobile();
  const { promoActions: promos, promoTournaments: tournamentPromos, getPromoByPromoId } = useSiteContent();
  const promoCategories = useMemo(() => getPromoCategories(locale, tr), [locale, tr]);
  const promoFilters = useMemo(() => getPromoFilters(tr), [tr]);
  const trackRef = useRef(null);
  const [category, setCategory] = useState(promoCategories[0]);
  const [displayCategory, setDisplayCategory] = useState(promoCategories[0]);
  const [filter, setFilter] = useState('all');
  const [displayFilter, setDisplayFilter] = useState('all');
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [activePromo, setActivePromo] = useState(null);
  const [showSkeletons, setShowSkeletons] = useState(false);
  const [skeletonCount, setSkeletonCount] = useState(6);
  const [trackFading, setTrackFading] = useState(false);

  const promoScrollLockedRef = useRef(false);

  const lockPromoScroll = useCallback(() => {
    if (promoScrollLockedRef.current) return;
    lockScroll();
    promoScrollLockedRef.current = true;
  }, []);

  const unlockPromoScroll = useCallback(() => {
    if (!promoScrollLockedRef.current) return;
    unlockScroll();
    promoScrollLockedRef.current = false;
  }, []);

  const openPromoModal = useCallback((promo) => {
    setActivePromo(promo);
    setPromoUrl(promo.promoId || promo.id);
    lockPromoScroll();
  }, [lockPromoScroll]);

  const openPromo = useCallback((promo) => {
    resolvePromoClick(promo, openPromoModal);
  }, [openPromoModal]);

  const closePromo = useCallback(() => {
    setActivePromo(null);
    setPromoUrl(null);
    unlockPromoScroll();
  }, [unlockPromoScroll]);

  useEffect(() => {
    const syncFromUrl = () => {
      const id = new URLSearchParams(window.location.search).get('promoId');
      const promo = getPromoByPromoId(id);
      if (promo) {
        setActivePromo((prev) => {
          const prevId = prev?.promoId || prev?.id || null;
          const nextId = promo?.promoId || promo?.id || null;
          if (prev && nextId && prevId === nextId) return prev;
          return promo;
        });
        lockPromoScroll();
        return;
      }
      setActivePromo(null);
      unlockPromoScroll();
    };

    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      unlockPromoScroll();
    };
  }, [getPromoByPromoId, lockPromoScroll, unlockPromoScroll]);

  useEffect(() => {
    setCategory(promoCategories[0]);
    setDisplayCategory(promoCategories[0]);
  }, [promoCategories]);

  const getItems = useCallback((cat, fil) => {
    const source = isTournamentCategory(cat, locale, tr) ? tournamentPromos : promos;
    return filterPromos(source, fil);
  }, [locale, tr, promos, tournamentPromos]);

  const items = useMemo(
    () => getItems(displayCategory, displayFilter),
    [displayCategory, displayFilter, getItems],
  );

  const switchView = useCallback((nextCategory, nextFilter) => {
    const nextItems = getItems(nextCategory, nextFilter);
    setSkeletonCount(Math.max(nextItems.length, 3));
    setShowSkeletons(true);
    setTrackFading(true);
    if (trackRef.current) trackRef.current.scrollLeft = 0;

    window.setTimeout(() => {
      setDisplayCategory(nextCategory);
      setDisplayFilter(nextFilter);
      setShowSkeletons(false);
      setTrackFading(false);
    }, 150);
  }, [getItems]);

  const switchCategory = (next) => {
    if (next === category) return;
    setCategory(next);
    switchView(next, filter);
  };

  const switchFilter = (next) => {
    if (next === filter) return;
    setFilter(next);
    switchView(category, next);
  };

  const updateScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    el.scrollLeft = 0;
    const run = () => updateScroll();
    run();
    requestAnimationFrame(run);
    const t = window.setTimeout(run, 150);
    el.addEventListener('scroll', updateScroll, { passive: true });
    const ro = new ResizeObserver(run);
    ro.observe(el);
    return () => {
      window.clearTimeout(tr);
      el.removeEventListener('scroll', updateScroll);
      ro.disconnect();
    };
  }, [updateScroll, items.length, displayCategory, displayFilter, showSkeletons]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const item = el.querySelector('.promo-carousel__item');
    const step = (item?.offsetWidth || 435) + 12;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <>
      <section className="promo-carousel">
        <div className="promo-carousel__section">
          <div className="promo-carousel__head">
            <div className="promo-carousel__categories">
              {promoCategories.map((c) => (
                <a
                  key={c}
                  href="#"
                  className={`promo-carousel__category${category === c ? ' is-current' : ''}`}
                  onClick={(e) => { e.preventDefault(); switchCategory(c); }}
                >
                  {c}
                </a>
              ))}
            </div>
            <div className="promo-carousel__filters">
              {promoFilters.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`promo-carousel__filter${filter === f.id ? ' is-current' : ''}`}
                  onClick={() => switchFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <a
              href="/promos/actions"
              className={`promo-carousel__show-all${isMobile ? ' promo-carousel__show-all--desktop' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                onNavigate?.('promos');
              }}
            >
              {tr('home.promo.showAll')}
            </a>
          </div>

          <div className="promo-carousel__container">
            <div className={`promo-carousel__track${trackFading ? ' is-fading' : ''}`} ref={trackRef}>
              {showSkeletons
                ? Array.from({ length: skeletonCount }, (_, i) => (
                  <div key={`promo-skeleton-${i}`} className="promo-carousel__item">
                    <PromoCardPlaceholder />
                  </div>
                ))
                : items.map((p) => (
                  <div key={p.id} className="promo-carousel__item">
                    <PromoCard promo={p} onDetails={openPromo} />
                  </div>
                ))}
            </div>
            {!isMobile && (
              <div className="promo-carousel__nav">
                <button
                  type="button"
                  className={`promo-carousel__nav-btn${!canLeft ? ' is-disabled' : ''}`}
                  aria-label="Previous"
                  disabled={!canLeft}
                  onClick={() => scroll(-1)}
                >
                  <IconChevronCircle direction="left" />
                </button>
                <button
                  type="button"
                  className="promo-carousel__nav-btn promo-carousel__nav-btn--next"
                  aria-label="Next"
                  disabled={!canRight}
                  onClick={() => scroll(1)}
                >
                  <IconChevronCircle direction="right" />
                </button>
              </div>
            )}
            {isMobile && (
              <a
                href="/promos/actions"
                className="mobile-tertiary-btn promo-carousel__all-btn"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate?.('promos');
                }}
              >
                {tr('home.promo.allPromotions')}
              </a>
            )}
          </div>
        </div>
      </section>
      {activePromo && (
        <PromoModalRouter promo={activePromo} onClose={closePromo} onRegister={onRegister} />
      )}
    </>
  );
}
